import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Spin
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  ProfileOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useBeforeUnload } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { articlesAPI } from '../utils/api';
import { generatePreviewHTML } from '../utils/previewGenerator';
import MediaGalleryModal from '../components/MediaGalleryModal';
import {
  UnsavedChangesModal,
  VideoSection,
  MediaSection,
  ArticleSidePanel
} from '../components/ArticleEditor';
import SectionKicker from '../components/ArticleEditor/SectionKicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Configurar dayjs en español
dayjs.locale('es');

const { Title, Text } = Typography;

const ArticleEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [content, setContent] = useState('');
  const [currentArticle, setCurrentArticle] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  
  // FIX: Estado para controlar actualizaciones del editor (previene race conditions en Chrome)
  const isUpdatingContent = useRef(false);
  const contentRef = useRef('');

  // Estados para video
  const [hasVideo, setHasVideo] = useState(false);
  const [videoType, setVideoType] = useState('url'); // 'url' o 'upload'
  const [videoUrl, setVideoUrl] = useState('');
  const [videoThumbnail, setVideoThumbnail] = useState('');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingVideoThumb, setUploadingVideoThumb] = useState(false);

  // Estado para galería de medios
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [galleryTarget, setGalleryTarget] = useState('header'); // 'header' o 'content'
  const quillRef = useRef(null);

  // Estados para control de cambios sin guardar
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const initialValuesRef = useRef(null);

  // Función para convertir <br> a saltos de línea (para retrocompatibilidad con artículos antiguos)
  const convertBrToNewLines = (text) => {
    if (!text) return text;
    return text.replace(/<br\s*\/?>/gi, '\n');
  };

  // FIX: Handler mejorado para cambios de contenido (previene borrado en Chrome)
  const handleContentChange = useCallback((value, delta, source, editor) => {
    // Evitar actualizaciones si estamos en proceso de actualización
    if (isUpdatingContent.current) {
      return;
    }

    // Si el cambio es de usuario (no programático)
    if (source === 'user') {
      // Actualizar el ref inmediatamente
      contentRef.current = value;
      
      // Actualizar el estado después de un pequeño delay para evitar race conditions
      requestAnimationFrame(() => {
        setContent(value);
      });
    } else {
      // Para cambios programáticos, actualizar directamente
      contentRef.current = value;
      setContent(value);
    }
  }, []);

  // Handler personalizado para insertar imagen desde galería
  const handleImageInsert = useCallback(() => {
    setGalleryTarget('content');
    setGalleryVisible(true);
  }, []);

  // Configuración del editor Quill (memorizada para evitar re-renders)
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        // Encabezados
        [{ 'header': [1, 2, 3, false] }],
        
        // Formato básico
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        
        // Alineación
        [{ 'align': [] }],
        
        // Listas
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        
        // Elementos especiales
        ['blockquote', 'code-block'],
        ['link', 'image'],
        
        // Herramientas
        ['clean']
      ],
      handlers: {
        image: handleImageInsert
      }
    },
    clipboard: {
      // FIX: Limpiar formato de Word y otros editores externos
      matchVisual: false // Importante para Chrome - evita que Quill intente preservar estilos visuales
    }
  }), [handleImageInsert]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  // Función para detectar y validar URLs de video
  const detectVideoType = (url) => {
    if (!url) return null;
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }
    if (url.includes('vimeo.com')) {
      return 'vimeo';
    }
    if (url.includes('.mp4')) {
      return 'mp4';
    }
    return null;
  };

  // Función para convertir URL de YouTube a formato embed
  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
  };

  // Función para convertir URL de Vimeo a formato embed
  const getVimeoEmbedUrl = (url) => {
    const videoId = url.match(/vimeo\.com\/([0-9]+)/);
    return videoId ? `https://player.vimeo.com/video/${videoId[1]}` : url;
  };

  // Procesar URL de video ingresada
  const handleVideoUrlChange = (url) => {
    setVideoUrl(url);
    
    if (url) {
      const detectedType = detectVideoType(url);
      if (detectedType === 'youtube') {
        const embedUrl = getYouTubeEmbedUrl(url);
        setVideoUrl(embedUrl);
      } else if (detectedType === 'vimeo') {
        const embedUrl = getVimeoEmbedUrl(url);
        setVideoUrl(embedUrl);
      }
    }
  };

  // Función para eliminar video
  const handleRemoveVideo = (type) => {
    if (type === 'thumbnail') {
      setVideoThumbnail('');
    } else if (type === 'all') {
      setHasVideo(false);
      setVideoUrl('');
      setVideoThumbnail('');
      setVideoType('url');
    }
  };

  // Cargar artículo existente si está editando
  const loadArticle = useCallback(async (articleId) => {
    setLoading(true);
    try {
      const response = await articlesAPI.getArticleById(articleId);
      
      if (response.success) {
        const article = response.data;
        setCurrentArticle(article);
        
        // FIX: Marcar que estamos actualizando programáticamente
        isUpdatingContent.current = true;
        
        // Establecer el contenido en el editor
        const articleContent = article.content || '';
        contentRef.current = articleContent;
        setContent(articleContent);
        
        // Establecer imagen y audio si existen
        setImageUrl(article.imageUrl || '');
        setAudioUrl(article.audioUrl || '');
        
        // Restaurar flag después de un momento
        setTimeout(() => {
          isUpdatingContent.current = false;
        }, 100);
      
        // Establecer datos de video si existen
        if (article.hasVideo) {
          setHasVideo(true);
          setVideoUrl(article.videoUrl || '');
          setVideoThumbnail(article.videoThumbnail || '');
          
          // Detectar el tipo de video basado en la URL
          const detectedType = detectVideoType(article.videoUrl);
          if (detectedType === 'mp4' || article.videoType === 'mp4') {
            setVideoType('upload');
          } else {
            setVideoType('url');
          }
        }
        
        // ✅ CRÍTICO: Usar setTimeout para asegurar que el DOM esté listo
        setTimeout(() => {
          form.setFieldsValue({
            title: article.title,
            subtitle: convertBrToNewLines(article.subtitle), // Convertir <br> a \n para el textarea
            section: article.section,
            date: article.date ? dayjs(article.date) : null
          });
          
          console.log('✅ Valores seteados:', {
            section: article.section,
            date: article.date
          });
        }, 300);
        
        message.success('Artículo cargado para edición');
      } else {
        message.error('No se pudo cargar el artículo');
        navigate('/articles');
      }
    } catch (error) {
      console.error('Error loading article:', error);
      message.error('Error al cargar el artículo');
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  }, [navigate, form]);

  // Cargar secciones
  const loadSections = async () => {
    try {
      const response = await articlesAPI.getSections();
      
      if (response.success) {
        // Los datos vienen como: { "Buenos días": [...], "Evangelio": [...], etc }
        const sectionsData = response.data;
        
        // Convertir las claves del objeto en un array de secciones
        const sectionNames = Object.keys(sectionsData).map(sectionName => ({
          section_name: sectionName,
          article_count: sectionsData[sectionName].length
        }));
        
        setSections(sectionNames);
      }
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  // Función para subir imagen a Bunny CDN
  const handleImageUpload = async (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
    if (!isJpgOrPng) {
      message.error('Solo puedes subir archivos JPG, PNG o WebP');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('La imagen debe ser menor a 5MB');
      return false;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
    
      // Obtener token de autenticación
      const token = localStorage.getItem('cms_token');
      
      // Upload real a Bunny CDN (directo al backend)
      const response = await fetch('https://api.appagustinos.es/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
    
      const result = await response.json();
      
      if (result.success) {
        setImageUrl(result.data.imageUrl);
        message.success('Imagen subida exitosamente');
      } else {
        message.error(result.error || 'Error al subir imagen');
      }

    } catch (error) {
      message.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }

    return false; // Prevenir upload automático de Ant Design
  };

// Función para subir audio a Bunny CDN (streaming directo - sin límite de tamaño)
const handleAudioUpload = async (file) => {
  const isAudio = file.type.startsWith('audio/');
  if (!isAudio) {
    message.error('Solo puedes subir archivos de audio');
    return false;
  }

  // Sin límite de tamaño - streaming directo a Bunny CDN vía servidor Hetzner
  console.log(`Subiendo audio de ${Math.round(file.size / 1024 / 1024)}MB...`);

  setUploadingAudio(true);

  try {
    // 1) Solicitar token temporal al servidor
    const tokenResponse = await fetch(`https://api.appagustinos.es/api/upload/audio-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('cms_token') || ''}`,
      },
      body: JSON.stringify({
        ext: file.name.split('.').pop() || 'mp3',
        dir: 'audios'
      }),
    });

    if (!tokenResponse.ok) {
      const tErr = await tokenResponse.text().catch(() => '');
      throw new Error(`Token request failed: ${tErr || tokenResponse.status}`);
    }

    const { token } = await tokenResponse.json();

    // 2) Subir el fichero directamente (streaming)
    const uploadResponse = await fetch(`https://api.appagustinos.es/api/upload/audio?token=${encodeURIComponent(token)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'audio/mpeg',
        'Authorization': `Bearer ${localStorage.getItem('cms_token') || ''}`,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const uErr = await uploadResponse.text().catch(() => '');
      throw new Error(`Upload failed: ${uErr || uploadResponse.status}`);
    }

    const result = await uploadResponse.json();
    
    if (result.success) {
      // 3) Guardar URL final (CDN)
      setAudioUrl(result.data.audioUrl);
      message.success('Audio subido exitosamente');
    } else {
      throw new Error(result.error || 'Error en la respuesta del servidor');
    }
  } catch (error) {
    console.error('Error al subir el audio:', error);
    message.error(error.message || 'Error al subir el audio');
  } finally {
    setUploadingAudio(false);
  }

  // Evitar que Ant Design haga upload automático
  return false;
};

  // Función auxiliar para generar thumbnail automáticamente desde el video
  const generateVideoThumbnail = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Configuración importante para cargar el video correctamente
      video.preload = 'auto'; // Cambiar de 'metadata' a 'auto' para cargar más datos
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      
      let hasAttempted = false;
      let videoUrl = null;
      
      video.onloadeddata = () => {
        // Esperar un momento adicional para asegurar que el video esté decodificado
        setTimeout(() => {
          if (!hasAttempted) {
            hasAttempted = true;
            // Capturar frame al segundo 1.5 (o 15% del video, lo que sea menor)
            const captureTime = Math.min(1.5, video.duration * 0.15);
            console.log(`🎬 Generando thumbnail en el segundo ${captureTime.toFixed(2)} de ${video.duration.toFixed(2)}s`);
            video.currentTime = captureTime;
          }
        }, 500); // Esperar 500ms después de que los datos se carguen
      };
      
      video.onseeked = () => {
        // Esperar un frame adicional para asegurar que el video esté pintado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              // Configurar canvas con las dimensiones del video
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              
              console.log(`📐 Dimensiones del video: ${canvas.width}x${canvas.height}`);
              
              if (canvas.width === 0 || canvas.height === 0) {
                throw new Error('Dimensiones del video inválidas');
              }
              
              // Dibujar el frame actual del video en el canvas
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              // Verificar si el canvas tiene contenido (no está completamente negro)
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const data = imageData.data;
              let sumBrightness = 0;
              
              // Calcular brillo promedio (muestrear cada 100 píxeles para rendimiento)
              for (let i = 0; i < data.length; i += 400) { // RGBA = 4 bytes, entonces 100 píxeles = 400
                sumBrightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
              }
              
              const avgBrightness = sumBrightness / (data.length / 400);
              console.log(`💡 Brillo promedio del thumbnail: ${avgBrightness.toFixed(2)}`);
              
              if (avgBrightness < 10) {
                console.warn('⚠️ El thumbnail parece estar muy oscuro, intentando con otro momento...');
                
                // Intentar con la mitad del video
                if (video.currentTime < video.duration * 0.4) {
                  video.currentTime = video.duration * 0.5;
                  return;
                }
              }
              
              // Convertir canvas a Blob (imagen)
              canvas.toBlob((blob) => {
                if (blob) {
                  console.log('✅ Thumbnail generado exitosamente');
                  // Crear un archivo File desde el Blob
                  const thumbnailFile = new File([blob], `thumbnail_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  resolve(thumbnailFile);
                } else {
                  reject(new Error('No se pudo generar el thumbnail'));
                }
                
                // Limpiar
                if (videoUrl) {
                  URL.revokeObjectURL(videoUrl);
                }
              }, 'image/jpeg', 0.90); // Aumentar calidad a 90%
              
            } catch (error) {
              console.error('❌ Error generando thumbnail:', error);
              if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
              }
              reject(error);
            }
          });
        });
      };
      
      video.onerror = (e) => {
        console.error('❌ Error al cargar el video:', e);
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
        reject(new Error('Error al cargar el video para generar thumbnail'));
      };
      
      // Crear URL temporal del archivo
      videoUrl = URL.createObjectURL(file);
      video.src = videoUrl;
      
      // Timeout de seguridad (30 segundos)
      setTimeout(() => {
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
        reject(new Error('Timeout: El video tardó demasiado en cargar'));
      }, 30000);
    });
  };

  // Función para subir video MP4 a Bunny CDN (servidor de Hetzner)
  const handleVideoUpload = async (file) => {
    const isVideo = file.type?.startsWith('video/');
    if (!isVideo) {
      message.error('Solo puedes subir archivos de video');
      return false;
    }

    // Sin límite de tamaño - streaming directo a Bunny CDN vía servidor Hetzner
    console.log(`Subiendo video de ${Math.round(file.size / 1024 / 1024)}MB...`);

    setUploadingVideo(true);

    try {
      // 1) Solicitar token temporal al servidor
      const tokenResponse = await fetch(`https://api.appagustinos.es/api/upload/video-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cms_token') || ''}`,
        },
        body: JSON.stringify({
          ext: 'mp4',
          dir: 'videos'
        }),
      });

      if (!tokenResponse.ok) {
        const tErr = await tokenResponse.text().catch(() => '');
        throw new Error(`Token request failed: ${tErr || tokenResponse.status}`);
      }

      const { token } = await tokenResponse.json();

      // 2) Subir el fichero directamente (streaming)
      const uploadResponse = await fetch(`https://api.appagustinos.es/api/upload/video?token=${encodeURIComponent(token)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'video/mp4',
          'Authorization': `Bearer ${localStorage.getItem('cms_token') || ''}`,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        const uErr = await uploadResponse.text().catch(() => '');
        throw new Error(`Upload failed: ${uErr || uploadResponse.status}`);
      }

      const result = await uploadResponse.json();
      
      if (result.success) {
        // 3) Guardar URL final (CDN)
        setVideoUrl(result.data.videoUrl);
        message.success('Video subido exitosamente');
        
        // 4) Generar thumbnail automáticamente del video
        try {
          message.loading('Generando thumbnail automático...', 0);
          const thumbnailFile = await generateVideoThumbnail(file);
          
          // Subir el thumbnail generado
          await handleVideoThumbnailUpload(thumbnailFile);
          message.destroy();
          message.success('Thumbnail generado automáticamente');
        } catch (thumbnailError) {
          message.destroy();
          console.warn('No se pudo generar thumbnail automático:', thumbnailError);
          message.info('Video subido. Puedes agregar un thumbnail manualmente si lo deseas.');
        }
      } else {
        throw new Error(result.error || 'Error en la respuesta del servidor');
      }
    } catch (error) {
      console.error('Error al subir el video:', error);
      message.error(error.message || 'Error al subir el video');
    } finally {
      setUploadingVideo(false);
    }

    // Evitar que Ant Design haga upload automático
    return false;
  };

  // ======== NUEVO: Subida de thumbnail de video ========
  const handleVideoThumbnailUpload = async (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
    if (!isJpgOrPng) {
      message.error('Solo puedes subir archivos JPG, PNG o WebP para el thumbnail');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('El thumbnail debe ser menor a 2MB');
      return false;
    }

    setUploadingVideoThumb(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
    
      const token = localStorage.getItem('cms_token');
      
      // Upload directo al backend
      const response = await fetch('https://api.appagustinos.es/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
    
      const result = await response.json();
      
      if (result.success) {
        setVideoThumbnail(result.data.imageUrl);
        message.success('Thumbnail de video subido exitosamente');
      } else {
        message.error(result.error || 'Error al subir thumbnail');
      }

    } catch (error) {
      message.error('Error al subir el thumbnail');
    } finally {
      setUploadingVideoThumb(false);
    }

    return false;
  };

  // ======== NUEVO: Vista previa de video ========
  const renderVideoPreview = () => {
    if (!hasVideo || !videoUrl) return null;

    const detectedType = detectVideoType(videoUrl);

    if (detectedType === 'youtube' || detectedType === 'vimeo') {
      return (
        <div style={{ marginTop: 16 }}>
          <Text strong>Vista previa del video:</Text>
          <div style={{ marginTop: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
            <iframe
              width="100%"
              height="315"
              src={videoUrl}
              frameBorder="0"
              allowFullScreen
              title="Video preview"
            />
          </div>
        </div>
      );
    } else if (detectedType === 'mp4') {
      return (
        <div style={{ marginTop: 16 }}>
          <Text strong>Vista previa del video:</Text>
          <div style={{ marginTop: 8 }}>
            <video
              width="100%"
              height="315"
              controls
              style={{ border: '1px solid #d9d9d9', borderRadius: 4 }}
            >
              <source src={videoUrl} type="video/mp4" />
              Tu navegador no soporta el elemento video.
            </video>
          </div>
        </div>
      );
    }

    return null;
  };

  // Función previsualización del artículo
  const openPreview = () => {
    const formValues = form.getFieldsValue();
    
    const previewData = {
      title: formValues.title || 'Título del artículo',
      subtitle: formValues.subtitle || '',
      content: content || '<p>Contenido del artículo...</p>',
      section: formValues.section || 'General',
      imageUrl: imageUrl || '',
      hasAudio: Boolean(audioUrl),
      audioUrl: audioUrl || '',
      hasVideo: hasVideo,
      videoUrl: videoUrl || '',
      videoType: videoType,
      videoThumbnail: videoThumbnail || '',
      date: formValues.date ? formValues.date.toISOString() : new Date().toISOString(),
      author: 'Agustinos'
    };
  
    // Generar HTML usando la función externa
    const previewHTML = generatePreviewHTML(previewData);
    
    // Abrir nueva pestaña con el contenido
    const newWindow = window.open('', '_blank');
    newWindow.document.write(previewHTML);
    newWindow.document.close();
    newWindow.focus();
  };

  // Manejar navegación con confirmación
  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowExitModal(true);
      setPendingNavigation('/articles');
    } else {
      navigate('/articles');
    }
  };

  // Continuar sin guardar
  const handleContinueWithoutSaving = () => {
    setHasUnsavedChanges(false);
    setShowExitModal(false);
    if (pendingNavigation) {
      if (pendingNavigation === -1) {
        // Navegación hacia atrás del navegador
        window.history.back();
      } else {
        // Navegación normal con React Router
        navigate(pendingNavigation);
      }
    }
  };

  // Guardar antes de salir
  const handleSaveAndExit = async () => {
    try {
      // Verificar campos requeridos
      await form.validateFields(['title']);
      
      // Guardar el artículo
      setSaving(true);
      
      const values = form.getFieldsValue();
      
      // Determinar el tipo real de video
      let finalVideoType = null;
      if (hasVideo && videoUrl) {
        if (videoType === 'upload') {
          finalVideoType = 'mp4';
        } else {
          const detectedType = detectVideoType(videoUrl);
          finalVideoType = detectedType;
        }
      }

      // Asegurar que date SIEMPRE tenga un valor válido
      let finalDate;
      if (values.date && dayjs.isDayjs(values.date)) {
        finalDate = values.date.toISOString();
      } else if (isEditing && currentArticle?.date) {
        finalDate = currentArticle.date;
      } else {
        finalDate = new Date().toISOString();
      }

      // Determinar el status
      let finalStatus;
      if (values.date && dayjs(values.date).isAfter(dayjs())) {
        finalStatus = 'scheduled';
      } else {
        finalStatus = 'published';
      }

      const articleData = {
        title: values.title,
        subtitle: values.subtitle || null,
        content: content,
        section: values.section || 'General',
        imageUrl: imageUrl || null,
        hasAudio: Boolean(audioUrl),
        audioUrl: audioUrl || null,
        hasVideo: hasVideo,
        videoUrl: hasVideo ? videoUrl || null : null,
        videoType: hasVideo ? finalVideoType : null,
        videoThumbnail: hasVideo ? videoThumbnail || null : null,
        date: finalDate,
        status: finalStatus
      };

      let response;
      if (isEditing) {
        response = await articlesAPI.updateArticle(id, articleData);
      } else {
        response = await articlesAPI.createArticle(articleData);
      }

      if (response.success) {
        message.success('Artículo guardado exitosamente');
        setHasUnsavedChanges(false);
        setShowExitModal(false);
        if (pendingNavigation) {
          if (pendingNavigation === -1) {
            // Navegación hacia atrás del navegador
            window.history.back();
          } else {
            // Navegación normal con React Router
            navigate(pendingNavigation);
          }
        }
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('El título es obligatorio para guardar');
      } else {
        message.error('Error al guardar el artículo');
        console.error('Error guardando antes de salir:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  // Cancelar salida
  const handleCancelExit = () => {
    setShowExitModal(false);
    setPendingNavigation(null);
  };

  // Guardar artículo (crear, actualizar o borrador)
  const handleSave = async (isDraft = false) => {
    try {
      const values = await form.validateFields();
      
      console.log('📝 Valores del formulario:', values);
      console.log('📝 Guardando como borrador:', isDraft);
      
      // Permitir guardar sin contenido
      // if (!content.trim()) {
      //   message.error('El contenido es obligatorio');
      //   return;
      // }
  
      setSaving(true);
  
      // Determinar el tipo real de video
      let finalVideoType = null;
      if (hasVideo && videoUrl) {
        if (videoType === 'upload') {
          finalVideoType = 'mp4';
        } else {
          finalVideoType = detectVideoType(videoUrl);
        }
      }
  
      // Asegurar que date SIEMPRE tenga un valor válido
      let finalDate;
      if (values.date && dayjs.isDayjs(values.date)) {
        finalDate = values.date.toISOString();
      } else if (isEditing && currentArticle?.date) {
        finalDate = currentArticle.date;
      } else {
        finalDate = new Date().toISOString();
      }
  
      // 🆕 Determinar el status basado en si es borrador o no
      let finalStatus;
      if (isDraft) {
        // Si se guarda como borrador, siempre es 'draft'
        finalStatus = 'draft';
      } else if (values.date && dayjs(values.date).isAfter(dayjs())) {
        // Si tiene fecha futura, es 'scheduled'
        finalStatus = 'scheduled';
      } else {
        // Si no, es 'published'
        finalStatus = 'published';
      }
  
      const articleData = {
        title: values.title,
        subtitle: values.subtitle || null,
        content: content,
        section: values.section || 'General',
        imageUrl: imageUrl || null,
        hasAudio: Boolean(audioUrl),
        audioUrl: audioUrl || null,
        hasVideo: hasVideo,
        videoUrl: hasVideo ? videoUrl || null : null,
        videoType: hasVideo ? finalVideoType : null,
        videoThumbnail: hasVideo ? videoThumbnail || null : null,
        date: finalDate,
        status: finalStatus
      };
  
      let response;
      
      if (isEditing) {
        response = await articlesAPI.updateArticle(id, articleData);
        message.success(isDraft ? 'Borrador guardado' : 'Artículo actualizado exitosamente');
      } else {
        response = await articlesAPI.createArticle(articleData);
        message.success(isDraft ? 'Borrador guardado' : 'Artículo creado exitosamente');
      }
  
      if (response.success) {
        // Marcar que no hay cambios sin guardar
        setHasUnsavedChanges(false);
        
        // Actualizar valores iniciales
        initialValuesRef.current = {
          title: values.title || '',
          subtitle: values.subtitle || '',
          section: values.section || '',
          date: values.date ? values.date.toISOString() : null,
          content: content || '',
          imageUrl: imageUrl || '',
          audioUrl: audioUrl || '',
          hasVideo: hasVideo,
          videoUrl: videoUrl || '',
          videoThumbnail: videoThumbnail || ''
        };
        
        navigate('/articles');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('Por favor completa todos los campos requeridos');
      } else {
        message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el artículo`);
        console.error('Error saving article:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  // Función para comparar valores y detectar cambios
  const checkForChanges = useCallback(() => {
    if (!initialValuesRef.current) return false;

    const currentValues = {
      title: form.getFieldValue('title') || '',
      subtitle: form.getFieldValue('subtitle') || '',
      section: form.getFieldValue('section') || '',
      date: form.getFieldValue('date') ? form.getFieldValue('date').toISOString() : null,
      content: content || '',
      imageUrl: imageUrl || '',
      audioUrl: audioUrl || '',
      hasVideo: hasVideo,
      videoUrl: videoUrl || '',
      videoThumbnail: videoThumbnail || ''
    };

    const initial = initialValuesRef.current;

    // Comparar cada campo
    const hasChanges = 
      currentValues.title !== initial.title ||
      currentValues.subtitle !== initial.subtitle ||
      currentValues.section !== initial.section ||
      currentValues.date !== initial.date ||
      currentValues.content !== initial.content ||
      currentValues.imageUrl !== initial.imageUrl ||
      currentValues.audioUrl !== initial.audioUrl ||
      currentValues.hasVideo !== initial.hasVideo ||
      currentValues.videoUrl !== initial.videoUrl ||
      currentValues.videoThumbnail !== initial.videoThumbnail;

    return hasChanges;
  }, [form, content, imageUrl, audioUrl, hasVideo, videoUrl, videoThumbnail]);

  // Detectar cambios en el formulario
  useEffect(() => {
    const hasChanges = checkForChanges();
    setHasUnsavedChanges(hasChanges);
  }, [form, content, imageUrl, audioUrl, hasVideo, videoUrl, videoThumbnail, checkForChanges]);

  // Prevenir salida del navegador si hay cambios (cerrar pestaña/ventana)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Prevenir navegación con botón "atrás" del navegador
  useBeforeUnload(
    React.useCallback(() => {
      if (hasUnsavedChanges) {
        return "Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?";
      }
    }, [hasUnsavedChanges])
  );

  // Interceptar navegación con React Router (botón atrás)
  useEffect(() => {
    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const confirmLeave = window.confirm(
          "Tienes cambios sin guardar. ¿Quieres guardarlos antes de salir?"
        );
        
        if (confirmLeave) {
          // Mostrar el modal en lugar de navegar
          setShowExitModal(true);
          setPendingNavigation(-1); // -1 indica navegación hacia atrás
          // Restaurar el historial
          window.history.pushState(null, '', window.location.href);
        } else {
          // Permitir la navegación
          setHasUnsavedChanges(false);
          window.history.back();
        }
      }
    };

    // Agregar un estado al historial para detectar el back
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  // Establecer valores iniciales después de cargar artículo o al iniciar nuevo
  useEffect(() => {
    // Solo establecer valores iniciales una vez al cargar
    if (!initialValuesRef.current) {
      const timer = setTimeout(() => {
        initialValuesRef.current = {
          title: form.getFieldValue('title') || '',
          subtitle: form.getFieldValue('subtitle') || '',
          section: form.getFieldValue('section') || '',
          date: form.getFieldValue('date') ? form.getFieldValue('date').toISOString() : null,
          content: content || '',
          imageUrl: imageUrl || '',
          audioUrl: audioUrl || '',
          hasVideo: hasVideo,
          videoUrl: videoUrl || '',
          videoThumbnail: videoThumbnail || ''
        };
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentArticle, isEditing, form, content, imageUrl, audioUrl, hasVideo, videoUrl, videoThumbnail]);

  useEffect(() => {
    loadSections();
    
    // Si está editando, cargar el artículo
    if (isEditing && id) {
      loadArticle(id);
    }
  }, [isEditing, id, loadArticle]);

  // FIX: Efecto para configurar el editor de Quill con handlers especiales para Chrome
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      
      if (quill) {
        // Prevenir el comportamiento por defecto de composición que causa problemas en Chrome
        const editorElement = quill.root;
        
        // FIX: Deshabilitar autocorrect y autocomplete que causan problemas en Chrome
        editorElement.setAttribute('autocorrect', 'off');
        editorElement.setAttribute('autocapitalize', 'off');
        editorElement.setAttribute('spellcheck', 'true'); // Mantener spellcheck pero sin autocorrect
        
        // FIX: Mejorar el manejo del clipboard
        const Delta = quill.constructor.import('delta');
        quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
          // Limpiar texto pegado de caracteres problemáticos
          if (typeof node.data === 'string') {
            const cleanText = node.data
              .replace(/\r\n/g, '\n') // Normalizar line breaks de Windows
              .replace(/\r/g, '\n');   // Normalizar line breaks de Mac antiguo
            return new Delta().insert(cleanText);
          }
          return delta;
        });
        
        console.log('✅ Editor Quill configurado con fixes para Chrome/Windows');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reinicializar el editor cuando cambie el contenido
  useEffect(() => {
    if (content && isEditing) {
      // Pequeño delay para asegurar que el DOM esté listo
      const timer = setTimeout(() => {
        const quillElement = document.querySelector('.ql-toolbar');
        if (quillElement) {
          quillElement.style.display = 'block';
          quillElement.style.visibility = 'visible';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [content, isEditing]);

  if (loading) {
    return (
      <div style={{ 
        padding: '24px', 
        background: '#f0f2f5', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Cargando artículo...</div>
      </div>
    );
  }

  return (
      <div style={{ 
        padding: '24px', 
        background: '#f0f2f5', 
        minHeight: '100vh' 
      }}>
        {/* Header */}
        <Row className="cms-header-row" justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space className="editor-title-group">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleNavigateBack}
              >
                Volver a Artículos
              </Button>
              <Title level={2} style={{ margin: 0 }}>
                {isEditing ? `Editar: ${currentArticle?.title || 'Artículo'}` : 'Nuevo Artículo'}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space className="cms-header-actions">
              <Button
                icon={<EyeOutlined />}
                onClick={openPreview}
                size="large"
                style={{
                  borderColor: '#e2e5e8',
                  color: '#3f4550'
                }}
              >
                Vista Previa
              </Button>
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => handleSave(true)}
                size="large"
                style={{
                  borderColor: '#e2e5e8',
                  color: '#3f4550'
                }}
              >
                Guardar Borrador
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => handleSave(false)}
                size="large"
              >
                {isEditing ? 'Actualizar y Publicar' : 'Publicar Artículo'}
              </Button>
            </Space>
          </Col>
        </Row>

            <Form
              form={form}
              layout="vertical"
              size="large"
            >
              <Row gutter={24}>
                {/* Editor Principal */}
                <Col xs={24} lg={18}>
                  <Card>
              {/* Sección: Información Básica */}
              <div style={{ 
                marginBottom: 24, 
                paddingBottom: 16, 
                borderBottom: '2px solid #f0f0f0' 
              }}>
                <SectionKicker icon={ProfileOutlined}>Información Básica</SectionKicker>
                
                {/* Título */}
                <Form.Item
                  name="title"
                  label={
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: '#262626'
                    }}>
                      Título del artículo
                    </span>
                  }
                  rules={[
                    { required: true, message: 'El título es obligatorio' },
                    { min: 5, message: 'El título debe tener al menos 5 caracteres' }
                  ]}
                >
                  <Input 
                    placeholder="Escribe un título atractivo..."
                    style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #d9d9d9'
                    }}
                  />
                </Form.Item>

                {/* Subtítulo (opcional) */}
                <Form.Item
                  name="subtitle"
                  label={
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: '#595959'
                    }}>
                      Subtítulo (opcional) - Presiona Enter para múltiples líneas
                    </span>
                  }
                >
                  <Input.TextArea 
                    placeholder="Subtítulo del artículo... (Presiona Enter para agregar líneas)"
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    style={{ 
                      padding: '10px 14px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}
                  />
                </Form.Item>
              </div>

              {/* Sección: Contenido Multimedia */}
              <MediaSection
                imageUrl={imageUrl}
                audioUrl={audioUrl}
                uploadingImage={uploadingImage}
                uploadingAudio={uploadingAudio}
                onImageUpload={handleImageUpload}
                onAudioUpload={handleAudioUpload}
                onImageRemove={() => setImageUrl('')}
                onAudioRemove={() => setAudioUrl('')}
                onGalleryOpen={() => {
                  setGalleryTarget('header');
                  setGalleryVisible(true);
                }}
              />

              {/* Sección: Video */}
              <VideoSection
                hasVideo={hasVideo}
                videoType={videoType}
                videoUrl={videoUrl}
                videoThumbnail={videoThumbnail}
                uploadingVideo={uploadingVideo}
                uploadingVideoThumb={uploadingVideoThumb}
                onHasVideoChange={setHasVideo}
                onVideoTypeChange={setVideoType}
                onVideoUrlChange={handleVideoUrlChange}
                onVideoUpload={handleVideoUpload}
                onVideoThumbnailUpload={handleVideoThumbnailUpload}
                onRemoveVideo={handleRemoveVideo}
                detectVideoType={detectVideoType}
                renderVideoPreview={renderVideoPreview}
              />

              {/* Sección: Contenido Principal */}
              <div style={{ 
                marginBottom: 24, 
                paddingBottom: 16, 
                borderBottom: '2px solid #f0f0f0' 
              }}>
                <SectionKicker icon={EditOutlined}>Contenido Principal</SectionKicker>

                <Form.Item
                  label={
                    <span style={{ 
                      fontSize: '17px', 
                      fontWeight: 'bold',
                      color: '#262626',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      📄 Contenido del artículo
                      <span style={{ 
                        color: '#8c8c8c', 
                        fontSize: '14px',
                        marginLeft: 4,
                        fontWeight: 'normal'
                      }}>
                        (opcional)
                      </span>
                    </span>
                  }
                >
                <div 
                  className="quill-editor-wrapper"
                  style={{ 
                    border: '2px solid #d9d9d9', 
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                >
                  <ReactQuill
                    ref={quillRef}
                    key={`quill-${isEditing ? id : 'new'}`}
                    theme="snow"
                    value={content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Escribe el contenido de tu artículo aquí..."
                    preserveWhitespace
                  />
                </div>
                </Form.Item>
              </div>
            </Card>
          </Col>

          {/* Panel lateral */}
          <Col xs={24} lg={6}>
            <ArticleSidePanel
              sections={sections}
              isEditing={isEditing}
              currentArticle={currentArticle}
              form={form}
              imageUrl={imageUrl}
              audioUrl={audioUrl}
              hasVideo={hasVideo}
              videoUrl={videoUrl}
              videoThumbnail={videoThumbnail}
              content={content}
              detectVideoType={detectVideoType}
            />
          </Col>
        </Row>
        </Form>

        {/* Modal de Galería de Medios */}
        <MediaGalleryModal
          visible={galleryVisible}
          onClose={() => {
            setGalleryVisible(false);
            setGalleryTarget('header');
          }}
          onSelectImage={(url) => {
            if (galleryTarget === 'header') {
              // Insertar como imagen de cabecera
              setImageUrl(url);
              message.success('Imagen seleccionada desde la galería');
            } else if (galleryTarget === 'content') {
              // Insertar en el contenido del editor
              const quill = quillRef.current?.getEditor();
              if (quill) {
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', url);
                quill.setSelection(range.index + 1);
                message.success('Imagen insertada en el contenido');
              }
            }
            setGalleryVisible(false);
            setGalleryTarget('header');
          }}
        />

        {/* Modal de confirmación para cambios sin guardar */}
        <UnsavedChangesModal
          visible={showExitModal}
          saving={saving}
          onSave={handleSaveAndExit}
          onContinueWithoutSaving={handleContinueWithoutSaving}
          onCancel={handleCancelExit}
        />
      </div>
  );
};

export default ArticleEditor;